import React, { useCallback, useEffect, useRef } from 'react';
import Typography, { TypographyProps } from '@mui/material/Typography';
import Box from '@mui/material/Box';

interface ResponsiveTypographyProps extends TypographyProps {
  text: string;
  alignLeft?: boolean;
  maxFontSize?: number; // in px (0.875rem * 16px)
  minFontSize?: number; // in px
}

const isOverflown = (container: HTMLElement, textEl: HTMLElement) => {
  return (
    textEl.getBoundingClientRect().height > container.getBoundingClientRect().height ||
    textEl.getBoundingClientRect().width > container.getBoundingClientRect().width
  );
};

const ResponsiveTypography: React.FC<ResponsiveTypographyProps> = ({
  text,
  maxFontSize = 120,
  minFontSize = 10,
  alignLeft,
  ...props
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);

  const rafId = useRef<number | null>(null);
  const debounceTimeout = useRef<number | null>(null);
  const lastHeight = useRef<number>(0);

  const updateFontSize = useCallback(() => {
    const container = containerRef.current;
    const textEl = textRef.current;

    if (!container || !textEl) return;
    if (container.clientHeight === 0 || container.clientWidth === 0) return;

    let low = minFontSize;
    let high = maxFontSize;
    let best = minFontSize;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      textEl.style.fontSize = `${mid}px`;

      const overflow = isOverflown(container, textEl);

      if (!overflow) {
        best = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    textEl.style.fontSize = `${best}px`;
  }, [maxFontSize, minFontSize]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const scheduleUpdate = () => {
      // debounce during animations (like collapse)
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }

      debounceTimeout.current = window.setTimeout(() => {
        if (rafId.current) cancelAnimationFrame(rafId.current);

        rafId.current = requestAnimationFrame(() => {
          updateFontSize();
        });
      }, 50);
    };

    const observer = new ResizeObserver(() => {
      const height = container.clientHeight;

      // skip tiny changes to avoid excessive recalculation
      if (Math.abs(height - lastHeight.current) < 1) return;

      lastHeight.current = height;
      scheduleUpdate();
    });

    observer.observe(container);

    // initial run
    scheduleUpdate();

    return () => {
      observer.disconnect();

      if (rafId.current) cancelAnimationFrame(rafId.current);
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    };
  }, [maxFontSize, minFontSize, text, updateFontSize]);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: alignLeft ? 'flex-start' : 'center',
        alignItems: 'center',
        overflowY: 'hidden',
      }}
    >
      <Typography {...props} ref={textRef} style={{ lineHeight: 1 }}>
        {text}
      </Typography>
    </Box>
  );
};

export default ResponsiveTypography;
